"use client";

import { IntentPrefetchLink } from "@/components/navigation/IntentPrefetchLink";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/DropdownMenu";
import { Skeleton } from "@/components/ui/Skeleton";
import { textVariants } from "@/components/ui/Text";
import type { LucideIcon } from "lucide-react";
import { ChevronRight, MoreHorizontal } from "lucide-react";
import * as React from "react";
import cn from "twsx";

// Linear's page-header breadcrumb: 13/500, ancestors text-tertiary, current
// text-primary, 6px to 9px quaternary chevrons, 24px hover pills. Past four levels the
// middle collapses into a ⋯ menu.

export type BreadcrumbItem = {
  label?: React.ReactNode;
  href?: string;
  onClick?: () => void;
  icon?: LucideIcon;
  isLoading?: boolean;
};

const MAX_VISIBLE = 4;

const crumbClassName = cn(
  textVariants({ size: "md", weight: "medium", tone: "inherit" }),
  "inline-flex h-6 min-w-0 shrink items-center gap-1.5 rounded-control px-1 leading-none outline-none",
  "transition-colors duration-100 focus-visible:ring-1 focus-visible:ring-accent",
);

/** Use when a page header shows its ancestors as a trail. */
export function Breadcrumb({ items, currentAs = "span", className }: { items: BreadcrumbItem[]; currentAs?: "span" | "h1"; className?: string }) {
  const collapsed = items.length > MAX_VISIBLE ? items.slice(1, -2) : [];
  const visible = collapsed.length ? [items[0], null, ...items.slice(-2)] : items;

  return (
    <nav aria-label="Breadcrumb" className={cn("-ml-1 min-w-0", className)}>
      <ol className="flex min-w-0 items-center gap-0.5">
        {visible.map((item, index) => (
          <li key={index} className={cn("flex min-w-0 items-center gap-0.5", index === visible.length - 1 ? "shrink" : "shrink-2")}>
            {index > 0 && <ChevronRight aria-hidden="true" className="size-2.25 shrink-0 text-text-quaternary" strokeWidth={2.5} />}
            {item ? <Crumb item={item} current={index === visible.length - 1} currentAs={currentAs} /> : <CollapsedCrumbs items={collapsed} />}
          </li>
        ))}
      </ol>
    </nav>
  );
}

function Crumb({ item, current, currentAs = "span" }: { item: BreadcrumbItem; current: boolean; currentAs?: "span" | "h1" }) {
  if (item.isLoading) return <Skeleton size="xs" className="mx-1 w-16" />;

  const Icon = item.icon;
  const content = (
    <>
      {Icon && <Icon aria-hidden="true" className="size-3.5 shrink-0 text-text-tertiary" strokeWidth={1.75} />}
      <span className="truncate">{item.label}</span>
    </>
  );
  const className = cn(
    crumbClassName,
    current ? "text-text-primary" : "text-text-tertiary",
    (item.href || item.onClick) && "hover:bg-fill-hover-subtle hover:text-text-primary",
  );

  if (current && !item.onClick) {
    const Current = currentAs;
    return <Current aria-current="page" className={className}>{content}</Current>;
  }
  if (item.href) {
    return <IntentPrefetchLink href={item.href} className={className}>{content}</IntentPrefetchLink>;
  }
  if (item.onClick) {
    return <button type="button" onClick={item.onClick} aria-current={current ? "page" : undefined} className={className}>{content}</button>;
  }
  return <span className={className}>{content}</span>;
}

function CollapsedCrumbs({ items }: { items: BreadcrumbItem[] }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Show hidden breadcrumbs"
        className={cn(crumbClassName, "w-6 justify-center px-0 text-text-tertiary hover:bg-fill-hover-subtle hover:text-text-primary")}
      >
        <MoreHorizontal className="size-3.5" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {items.map((item, index) => (
          <DropdownMenuItem key={index} asChild={Boolean(item.href)} onSelect={item.onClick}>
            {item.href ? <IntentPrefetchLink href={item.href}>{item.label}</IntentPrefetchLink> : item.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
